/**
 * Shared shell for the v2 design.
 *
 * Wraps a route in a clean header (AMZEL logo + title + language selector +
 * light/dark toggle) and a footer with the Spreenauten "inspired by" mark.
 * Designed to be easy to read for non-IT users at AMZ: large headings, calm
 * slate palette, generous spacing.
 */
import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Languages } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";

import "./i18n";
import { ThemeProvider, ThemeToggle } from "./theme-provider";

interface ShellProps {
  children: ReactNode;
  /** Optional back-link, e.g. on detail pages. */
  back?: { to: string; label: string };
  /** Optional small kicker shown left of the title in the header. */
  kicker?: string;
}

export function ShellV2({ children, back, kicker }: Readonly<ShellProps>) {
  return (
    <ThemeProvider>
      <div className="min-h-screen bg-slate-50 text-slate-900 transition-colors dark:bg-slate-950 dark:text-slate-100">
        <Header back={back} kicker={kicker} />
        <main className="mx-auto w-full max-w-6xl px-4 sm:px-6 py-8 sm:py-10">{children}</main>
        <Footer />
      </div>
    </ThemeProvider>
  );
}

const LANGS = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "pl", label: "Polski", flag: "🇵🇱" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
] as const;

function LanguageSelector() {
  const { i18n, t } = useTranslation();
  const current = LANGS.some((l) => l.code === i18n.language) ? i18n.language : "en";
  return (
    <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white pl-3 pr-1 py-1 text-sm shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <Languages className="size-4 text-slate-500 dark:text-slate-400" aria-hidden />
      <span className="sr-only">{t("lang.label")}</span>
      <select
        value={current}
        onChange={(e) => void i18n.changeLanguage(e.target.value)}
        className="cursor-pointer appearance-none bg-transparent pr-2 text-sm font-medium text-slate-700 focus:outline-none dark:text-slate-200"
      >
        {LANGS.map((l) => (
          <option key={l.code} value={l.code}>
            {l.flag} {l.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Header({
  back,
  kicker,
}: Readonly<{ back?: { to: string; label: string }; kicker?: string }>) {
  const { t } = useTranslation();
  return (
    <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center gap-3 px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src="/amzel-logo.svg"
            alt="AMZEL"
            className="size-10 shrink-0"
          />
          <div className="flex flex-col leading-tight">
            <span className="text-base font-semibold tracking-tight">{t("shell.title")}</span>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {t("shell.subtitle")}
            </span>
          </div>
        </Link>

        {kicker && (
          <span className="ml-3 hidden rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 sm:inline-block dark:bg-slate-800 dark:text-slate-300">
            {kicker}
          </span>
        )}

        <div className="ml-auto flex items-center gap-2">
          {back && (
            <Link
              to={back.to}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <ArrowLeft className="size-4" aria-hidden />
              {back.label}
            </Link>
          )}
          <LanguageSelector />
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
}

function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="mt-16 border-t border-slate-200 bg-white/60 dark:border-slate-800 dark:bg-slate-950/60">
      <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-4 px-4 sm:px-6 py-6 sm:flex-row">
        <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
          <span>
            <Trans
              i18nKey="shell.footerInspiredBy"
              components={[
                <span
                  key="amazon"
                  className="font-amazon"
                />,
              ]}
            />
          </span>
          <a
            href="https://spreenauten.com"
            target="_blank"
            rel="noreferrer"
            aria-label="Spreenauten GmbH"
          >
            <img
              src="/spreenauten-logo-long.svg"
              alt="Spreenauten"
              className="h-4 w-auto dark:brightness-0 dark:invert"
            />
          </a>
        </div>
        <div className="text-xs text-slate-400 dark:text-slate-500">
          {t("shell.footerTag", { date: new Date().toISOString().slice(0, 10) })}
        </div>
      </div>
    </footer>
  );
}

